import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { fetcher } from '@dashboard/services/request';
import { pageKey } from '@dashboard/lib/queryKeys';
import { Form } from '@dashboard/components/Formik';
import { useTicketGroupHelpers } from '../useTicketGroupHelpers';

export default function TicketGroupSettingsForm({ ticketGroup, onClose }) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();
  const { onDirty } = useTicketGroupHelpers(ticketGroup._id);

  const editTicketGroupMutation = useMutation({
    mutationFn: values =>
      fetcher(
        ticketGroup?._id
          ? `/sellers/${sellerId}/ticket-groups/${ticketGroup._id}`
          : `/sellers/${sellerId}/ticket-groups`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    },
  });

  return (
    <Form
      onDirty={onDirty}
      initialValues={{
        name: ticketGroup?.name || '',
        description: ticketGroup?.description || '',
        preselect: {
          enabled: ticketGroup?.preselect?.enabled || false,
          ticketType: ticketGroup?.preselect?.ticketType || null,
          quantity: ticketGroup?.preselect?.quantity || null,
        },
        ticketSelectionType: ticketGroup.ticketSelectionType,
        capacity: ticketGroup?.capacity === null ? null : ticketGroup.capacity,
        hidden: ticketGroup?.hidden || false,
        reference: ticketGroup?.reference || '',
        //bulkDiscounts: ticketGroup?.bulkDiscounts || [],
      }}
      onSubmit={async (values, actions) => {
        try {
          const data = await editTicketGroupMutation.mutateAsync({
            ...values,
            pageId: page._id,
          });

          actions.resetForm({
            values: {
              name: data.name,
              description: data.description,
              preselect: {
                enabled: data.preselect.enabled,
                ticketType: data.preselect.ticketType,
                quantity: data.preselect.quantity,
              },
              ticketSelectionType: data.ticketSelectionType,
              capacity: data.capacity,
              hidden: data.hidden,
              reference: data.reference,
            },
          });

          return {
            data: data,
          };
        } catch (error) {
          return {
            error: error.message,
          };
        }
      }}
      fields={({ values }) => [
        {
          name: 'name',
          label: 'Group name',
          type: 'text',
          required: true,
          autoFocus: true,
          autoComplete: 'off',
        },
        {
          name: 'description',
          label: 'Short description',
          type: 'text',
        },
        {
          type: 'columns',
          columns: 2,
          fields: [
            {
              name: 'capacity',
              label: 'Group capacity',
              placeholder: 'Group capacity',
              type: 'number',
            },
          ],
        },
        {
          name: 'ticketSelectionType',
          label: 'Ticket selection',
          type: 'radio',
          options: [
            { label: 'Quantity select', value: 'quantity' },
            { label: 'Multiple select', value: 'multiple' },
            { label: 'Single select', value: 'single' },
          ],
        },

        {
          name: 'additionalSettings',
          type: 'disclosure',
          label: 'Additional settings',
          fields: [
            {
              name: 'preselect.enabled',
              label: 'Preselect ticket type',
              type: 'checkbox',
              noMargin: !values.preselect.enabled,
              reduceMargin: values.preselect.enabled,
            },
            {
              name: 'preselect.ticketType',
              type: 'select',
              options: [
                {
                  label: 'Select ticket',
                  value: '',
                  default: true,
                },
                ...ticketGroup.ticketTypes.map(t => ({
                  label: t.name,
                  value: t._id,
                })),
              ],
              visible: values => values.preselect.enabled,
              reduceMargin: true,
              inset: true,
            },
            {
              type: 'columns',
              columns: 2,
              fields: [
                {
                  name: 'preselect.quantity',
                  label: 'Preselect quantity',
                  placeholder: 'Quantity',
                  type: 'number',
                  inset: true,
                },
              ],
              visible: values =>
                values.preselect.enabled && values.ticketSelectionType === 'quantity',
            },
            {
              name: 'hidden',
              label: 'Hide ticket group',
              type: 'checkbox',
              tooltip: 'Hide this ticket group and display it via query parameters',
            },
            {
              name: 'reference',
              label: 'Reference',
              type: 'text',
            },
          ],
        },
      ]}
      cancelButton={{
        label: 'Cancel',
        onClick: onClose,
      }}
      formSubmitAlign="end"
    />
  );
}
